import { useState } from 'react';
import { Code2, Sparkles, RefreshCw } from 'lucide-react';
import { CODE_MODELS, CODE_LANGUAGES } from '../lib/models';
import PageHeader from '../components/ui/PageHeader';
import GlassCard from '../components/ui/GlassCard';
import Button from '../components/ui/Button';
import Select from '../components/ui/Select';
import PromptInput from '../components/generation/PromptInput';
import ModelSelector from '../components/generation/ModelSelector';
import GenerationProgress from '../components/generation/GenerationProgress';
import ResultReveal from '../components/generation/ResultReveal';
import CodeBlock from '../components/generation/CodeBlock';
import { useGenerate } from '../hooks/useGenerate';

const SAMPLES = [
  'A React hook that debounces a value',
  'Binary search over a sorted array',
  'Express middleware to validate a JWT',
];

export default function CodeStudio() {
  const [prompt, setPrompt] = useState('');
  const [model, setModel] = useState(CODE_MODELS[0].value);
  const [language, setLanguage] = useState('javascript');
  const { generate, loading, elapsed, result } = useGenerate('/generate/code');

  const run = () => prompt.trim() && generate({ prompt, model, language });

  return (
    <div>
      <PageHeader
        title="Code studio"
        subtitle="Generate code with StarCoder2 and Code Llama."
        icon={Code2}
      />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,420px)_1fr]">
        <div className="space-y-4">
          <PromptInput
            value={prompt}
            onChange={setPrompt}
            onSubmit={run}
            loading={loading}
            placeholder="A function that throttles calls to an async API…"
            actionLabel="Generate code"
          />
          <GlassCard className="space-y-4 p-4">
            <ModelSelector value={model} onChange={setModel} models={CODE_MODELS} />
            <Select
              label="Language"
              value={language}
              onChange={setLanguage}
              options={CODE_LANGUAGES}
            />
          </GlassCard>

          <div className="flex flex-wrap gap-2">
            {SAMPLES.map((s) => (
              <button
                key={s}
                onClick={() => setPrompt(s)}
                className="rounded-full border border-border/70 px-3 py-1.5 text-xs text-content-muted transition-colors hover:border-accent-fuchsia hover:text-content"
              >
                {s.slice(0, 26)}…
              </button>
            ))}
          </div>
        </div>

        <GlassCard spotlight className="flex min-h-[420px] flex-col justify-center p-4">
          {loading ? (
            <GenerationProgress elapsed={elapsed} label="Writing code" />
          ) : result ? (
            <ResultReveal className="w-full">
              <CodeBlock
                code={result.outputText}
                language={result.params?.language || language}
                maxHeight="58vh"
              />
              <div className="mt-4 flex items-center justify-between">
                <p className="line-clamp-1 max-w-[60%] text-sm text-content-muted">{result.prompt}</p>
                <Button variant="secondary" size="sm" icon={RefreshCw} onClick={run}>
                  Regenerate
                </Button>
              </div>
            </ResultReveal>
          ) : (
            <div className="flex flex-col items-center gap-3 text-center">
              <div className="rounded-2xl bg-accent-fuchsia/10 p-4 glow-pulse">
                <Sparkles className="h-8 w-8 text-accent-fuchsia" />
              </div>
              <p className="font-medium">Your code will appear here</p>
              <p className="max-w-xs text-sm text-content-muted">
                Describe what you need, choose a language, and generate.
              </p>
            </div>
          )}
        </GlassCard>
      </div>
    </div>
  );
}
